import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import { styled } from '@mui/material/styles';
import Switch from '@mui/material/Switch';
import { Tooltip } from 'components/Tooltip';
import React, { useState, useEffect } from 'react';
import { Row, Col } from 'reactstrap';

import { ColumnConfig } from '..';

const PREFIX = 'ColumnSelector';

const classes = {
  container: `${PREFIX}-container`,
  iconCell: `${PREFIX}-iconCell`,
  switchLabel: `${PREFIX}-switchLabel`,
};

const Root = styled('div')(({ theme }) => ({
  [`&.${classes.container}`]: {
    maxWidth: 400,
    maxHeight: 600,
    margin: 'auto',
    padding: theme.spacing(2),
    // backgroundColor: "#222a420d",
    borderRadius: 8,
    boxShadow: '0 0 10px rgba(0, 0, 0, 0.1)',
    overflowY: 'auto', // Add vertical scroll bars
  },
  [`& .${classes.iconCell}`]: {
    width: 48,
    textAlign: 'center',
  },
  [`& .${classes.switchLabel}`]: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: 0,
  },
}));

interface ColumnSelectorProps {
  columns: ColumnConfig[];
  enabledColumnKeys: string[];
  defaultColumnKeys?: string[];
  keysToBeExcluded?: string[];
  onColumnToggle: (enabledColumnKeys: string[]) => void;
}

const ColumnSelector: React.FC<ColumnSelectorProps> = ({
  columns,
  enabledColumnKeys,
  keysToBeExcluded,
  onColumnToggle,
}) => {
  const [columnState, setColumnState] = useState<Map<string, boolean>>(
    new Map(),
  );

  useEffect(() => {
    const initialColumnState = new Map<string, boolean>(
      enabledColumnKeys.map((key) => [key, true]),
    );

    // exclude the columns, which should not be displayed
    let effectiveColumns = columns?.filter(
      (column) => !keysToBeExcluded?.includes(column.key),
    );

    effectiveColumns.forEach((column) => {
      const key = column.key;
      if (!initialColumnState.has(key)) {
        initialColumnState.set(key, false);
      }
    });

    setColumnState(initialColumnState);
  }, [columns, enabledColumnKeys]);

  const toggleColumn = (key: string) => {
    const updatedColumnState = new Map(columnState);
    const currentValue = updatedColumnState.get(key);
    updatedColumnState.set(key, !currentValue);

    setColumnState(updatedColumnState);

    const enabledColumns = Array.from(updatedColumnState.entries())
      .filter(([key, value]) => value)
      .map(([key]) => key);

    onColumnToggle(enabledColumns);
  };

  const moveColumn = (key: string, direction: 'up' | 'down') => {
    const currentColumns = Array.from(columnState.keys());
    const currentIndex = currentColumns.indexOf(key);
    const newIndex =
      direction === 'up'
        ? currentIndex - 1
        : direction === 'down'
          ? currentIndex + 1
          : currentIndex;

    if (newIndex >= 0 && newIndex < currentColumns.length) {
      const newColumns = [...currentColumns];
      [newColumns[currentIndex], newColumns[newIndex]] = [
        newColumns[newIndex],
        newColumns[currentIndex],
      ];

      const updatedColumnState = new Map<string, boolean>(
        newColumns.map((columnKey) => [
          columnKey,
          columnState.get(columnKey) || false,
        ]),
      );

      setColumnState(updatedColumnState);
      const enabledColumns = newColumns.filter((key) => columnState.get(key));
      onColumnToggle(enabledColumns);
    }
  };

  return (
    <Row>
      <Col className="d-flex justify-content-center align-items-center">
        <span>
          Use toggle switches to show/hide columns and arrows to rearrange them.
        </span>
      </Col>
      <Col>
        <Root className={classes.container}>
          <table className="table table-striped table-hover">
            <tbody>
              {columnState &&
                Array.from(columnState.entries()).map(([key, value]) => {
                  const column = columns.find((column) => column.key === key);
                  return (
                    <tr key={key}>
                      <td>
                        <label className={classes.switchLabel}>
                          <Switch
                            color="primary"
                            checked={value}
                            onChange={() => toggleColumn(key)}
                          />
                          {/*  tooltip  to show the header tooltip if available */}
                          {column?.headerTooltip ? (
                            <Tooltip
                              // id={`data-grid-cs-${column.key}`}
                              title={column.label}
                              tooltip={column.headerTooltip}
                            >
                              {' '}
                              {column?.label}
                            </Tooltip>
                          ) : (
                            column?.label
                          )}
                        </label>
                      </td>
                      <td className={classes.iconCell}>
                        <ArrowUpwardIcon
                          onClick={() => moveColumn(key, 'up')}
                          style={{ cursor: 'pointer' }}
                        />
                      </td>
                      <td className={classes.iconCell}>
                        <ArrowDownwardIcon
                          onClick={() => moveColumn(key, 'down')}
                          style={{ cursor: 'pointer' }}
                        />
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </Root>
      </Col>
    </Row>
  );
};

export default ColumnSelector;
